import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy } from "@angular/core";
import { FlexModule } from "@angular/flex-layout/flex";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
	faArrowRight,
	faChartLine,
	faCogs,
	faCommentsDollar,
	faCreditCard,
	faHandshake,
	faLock,
	faPeopleGroup,
	faQuestionCircle,
	faTachometerAlt
} from "@fortawesome/free-solid-svg-icons";
import { createSelector, select, Store } from "@ngrx/store";
import { TranslatePipe } from "@ngx-translate/core";
import { filter, take } from "rxjs/operators";
import { SubSink } from "subsink";

import { TypedAppConfigService } from "../../../services/typed-app.config.service";
import { NavRequested } from "../../../store/actions/user.actions";
import { UserState } from "../../../store/models/user-state";
import { UserStatusState } from "../../../store/models/user-status-state";
import { AppState } from "../../../store/reducers";
import { MpCommonSelectors } from "../../../store/selectors/mp-common.selectors";
import { SelectUser } from "../../../store/selectors/user.selectors";
import { HowItWorksComponent } from "./how-it-works.component";
import { LandingPageFooterComponent } from "./landing-page-footer.component";

const selectUserInfo = createSelector(SelectUser, MpCommonSelectors.userStatus, (user, status) => {
	return {
		user,
		status,
		loading: user.loading || status.loading
	};
});

@Component({
	selector: "app-landing",
	templateUrl: "./landing.page.html",
	styleUrls: ["./landing-page-shared.scss", "./landing.page.scss"],
	imports: [
		FlexModule,
		MatButtonModule,
		MatToolbarModule,
		MatGridListModule,
		FontAwesomeModule,
		RouterLink,
		MatFormFieldModule,
		MatIconModule,
		MatCardModule,
		FormsModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatTooltipModule,
		HowItWorksComponent,
		LandingPageFooterComponent,
		TranslatePipe
	]
})
export class LandingPage implements AfterViewChecked, OnDestroy {
	protected readonly faQuestionCircle = faQuestionCircle;
	faCogs = faCogs;
	faTachometerAlt = faTachometerAlt;
	faChartLine = faChartLine;
	faLock = faLock;
	faCommentsDollar = faCommentsDollar;
	faCreditCard = faCreditCard;
	faHandshake = faHandshake;
	faPeopleGroup = faPeopleGroup;
	faArrowRight = faArrowRight;

	subs = new SubSink();
	loading = false;
	showAlert = false;
	model: { username?: string; password?: string } = {};

	forgotUsernameUrl;
	forgotPasswordUrl;
	loginHelpUrl;
	providerLogos;

	constructor(
		private store: Store<AppState>,
		private cdRef: ChangeDetectorRef,
		private appConfigService: TypedAppConfigService
	) {
		this.forgotUsernameUrl = this.appConfigService.getConfigValue("FORGOT_USERNAME_URL");
		this.forgotPasswordUrl = this.appConfigService.getConfigValue("FORGOT_PASSWORD_URL");
		this.loginHelpUrl = this.appConfigService.getConfigValue("LOGIN_HELP_URL");
		this.providerLogos = this.appConfigService.getConfigValue("LANDING_PAGE_LOGOS");
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	ngAfterViewChecked() {
		this.cdRef.detectChanges();
	}

	login() {
		this.loading = true;
		this.store.dispatch(NavRequested({ path: ["app", "dashboard"] }));
		this.subs.sink = this.store
			.pipe(
				select(selectUserInfo),
				filter((data) => !!data.user.error || (data.user.auth && !data.loading)),
				take(1)
			)
			.subscribe((data: { user: UserState; status: UserStatusState }) => {
				// Force a refresh so user's that don't ever refresh will get the latest version of the app.
				const needsRefresh = sessionStorage.getItem("needsRefresh");
				if (needsRefresh === "true") {
					sessionStorage.removeItem("needsRefresh");
					setTimeout(() => {
						window.location.reload();
					}, 300);
				} else {
					if (data?.user?.error) {
						this.loading = false;
						this.showAlert = true;
					} else {
						this.store.dispatch(NavRequested({ path: ["app"] }));
					}
				}
			});
	}
}
